import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { AlertTriangle } from 'lucide-react';

interface ConfirmationModalProps {
    isOpen: boolean;
    onConfirm: () => void;
    onCancel: () => void;
    title: string;
    message: string;
}

/**
 * A generic confirmation modal for user actions.
 * Used for Flatrack wooden dunnage warning and other confirmations.
 */
const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
    isOpen,
    onConfirm,
    onCancel,
    title,
    message,
}) => {
    const { t: translate } = useLanguage();

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-[#070910]/80 backdrop-blur-md animate-in fade-in duration-300">
            <div className="bg-white dark:bg-[#0D111C] rounded-2xl border border-slate-200 dark:border-white/10 shadow-2xl w-full max-w-md overflow-hidden transform transition-all">
                {/* Header */}
                <div className="px-5 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white">
                    <div className="flex items-center gap-3">
                        <div className="p-1.5 bg-white/20 rounded-lg">
                            <AlertTriangle className="w-6 h-6" />
                        </div>
                        <h3 className="text-lg font-bold">
                            {title}
                        </h3>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 text-slate-700 dark:text-orange-100">
                    <p className="text-base leading-relaxed text-center">
                        {message}
                    </p>
                </div>

                {/* Footer */}
                <div className="px-5 py-4 bg-slate-50 dark:bg-[#070910]/40 flex gap-3 justify-center border-t border-slate-200 dark:border-white/10">
                    <button
                        onClick={onCancel}
                        className="flex-1 px-6 py-2.5 text-sm font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-white/10 rounded-xl transition-all border border-slate-200 dark:border-white/10"
                    >
                        {translate('common.no')}
                    </button>
                    <button
                        onClick={onConfirm}
                        className="flex-1 px-6 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold text-sm rounded-xl shadow-lg shadow-orange-500/20 active:scale-[0.98] transition-all"
                    >
                        {translate('common.yes')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConfirmationModal;
